import { useMetricQuery } from "../useMetricQuery";
import { DropdownMenu } from "@/ui/organisms";
import { AccountLabel } from "@/entities/Labeling/Account.model";
import { formatMetricLabel } from "../metrics.helpers";
import clsx from "clsx";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { operationLabels, WindowOperation } from "../metrics.types";

export function MetricListHeader() {
    const {
        currentMetricQuery,
        setMetric,
        setPeriodResolution,
        removeWindow,
        addWindow,
        setWindowSize,
        setWindowApplyLast,
        setWindowOperation,
    } = useMetricQuery();
    const { metric, periodResolution, window } = currentMetricQuery;

    return (
        <div className="px-3">
            <div className={clsx("mt-4 w-full", "flex flex-col", "gap-2")}>
                <div className="flex items-center gap-4">
                    <DropdownMenu
                        className="body-s text-default"
                        items={Object.values(["m", "q", "y"]).map((label) => ({
                            type: "item",
                            id: label,
                            label:
                                label === "m"
                                    ? "Monthly"
                                    : label === "q"
                                      ? "Quarterly"
                                      : "Yearly",
                            onClick: () =>
                                setPeriodResolution(label as "m" | "q" | "y"),
                        }))}
                        selectedOptionId={periodResolution}
                    />
                    <DropdownMenu
                        className="body-s text-default"
                        items={Object.values(AccountLabel).map((label) => ({
                            type: "item",
                            id: label,
                            label: formatMetricLabel(label),
                            onClick: () => setMetric(label),
                        }))}
                        selectedOptionId={metric}
                    />
                </div>
                {!window && (
                    <div>
                        <button
                            className="quiet body-s p-0"
                            onClick={() =>
                                addWindow({
                                    size: 12,
                                    applyLast: false,
                                    operation: WindowOperation.AVERAGE,
                                })
                            }
                        >
                            Add window
                            <PlusIcon className="size-4" />
                        </button>
                    </div>
                )}
                {window && (
                    <div
                        className={clsx(
                            "mb-3 mt-2 w-full",
                            "flex flex-col",
                            "gap-2",
                        )}
                    >
                        <div className="flex items-center gap-2">
                            <p className="body-s text-default">
                                Window settings
                            </p>
                            <XMarkIcon
                                className="size-4 cursor-pointer stroke-red-400"
                                onClick={() => removeWindow()}
                            />
                        </div>
                        <div
                            className={clsx(
                                "px-2",
                                "flex items-center justify-between gap-2",
                            )}
                        >
                            <DropdownMenu
                                className="body-micro text-default"
                                items={Object.values(WindowOperation).map(
                                    (label) => ({
                                        type: "item",
                                        id: label,
                                        label: operationLabels[label],
                                        onClick: () =>
                                            setWindowOperation(
                                                label as WindowOperation,
                                            ),
                                    }),
                                )}
                                selectedOptionId={window.operation.toString()}
                            />
                            <DropdownMenu
                                className="body-micro text-default"
                                items={Object.values([true, false]).map(
                                    (label) => ({
                                        type: "item",
                                        id: label.toString(),
                                        label: label
                                            ? "Apply window first"
                                            : "Apply window last",
                                        onClick: () =>
                                            setWindowApplyLast(!label),
                                    }),
                                )}
                                selectedOptionId={(!window.applyLast).toString()}
                            />
                        </div>
                        <div className="flex items-center gap-2 px-2">
                            <label className="body-micro text-default">
                                Window Size
                            </label>
                            <input
                                type="number"
                                value={window.size?.toString() || ""}
                                min={1}
                                max={255}
                                onChange={(e) =>
                                    setWindowSize(Number(e.target.value))
                                }
                                className={clsx(
                                    "bg-secondary body-s w-[60px] rounded border-none p-1 px-2 outline-none",
                                    "placeholder:text-slate-500",
                                    "focus:ring-transparent focus:placeholder:text-transparent",
                                    (window.size === null ||
                                        window.size < 1 ||
                                        window.size > 255) &&
                                        "ml-px ring-1 ring-red-600",
                                )}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
